import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar, Car, Gauge, Shield, User, CreditCard, Phone, Mail, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { createBooking } from '../api';

const API = process.env.REACT_APP_BACKEND_URL;

const MILEAGE_PACKAGES = [
  { id: 'standard', label: 'Standard', detail: '200 mi/day included', price: 0 },
  { id: 'extended', label: 'Extended', detail: '300 mi/day', price: 50 },
  { id: 'unlimited', label: 'Unlimited', detail: 'No mileage cap', price: 150 },
];

const daysBetween = (a, b) => {
  if (!a || !b) return 0;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

const RentalBooking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get('vehicle') || '';

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    pickupDate: '',
    returnDate: '',
    mileagePackage: 'standard',
    protectionAddon: false,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    driversLicense: '',
    ageConfirmed: false,
    agreeTerms: false,
  });

  useEffect(() => {
    if (!vehicleId) {
      setLoading(false);
      return;
    }
    fetch(`${API}/api/pricing/vehicles`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.vehicles || []);
        const match = list.find(v => v.vehicle_id === vehicleId);
        setVehicle(match || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vehicleId]);

  const days = useMemo(() => daysBetween(form.pickupDate, form.returnDate), [form.pickupDate, form.returnDate]);
  const dailyRate = vehicle?.daily_rate || 0;
  const pkg = MILEAGE_PACKAGES.find(p => p.id === form.mileagePackage) || MILEAGE_PACKAGES[0];
  const mileageCost = pkg.price * days;
  const protectionCost = form.protectionAddon ? 45 * days : 0;
  const subtotal = dailyRate * days + mileageCost + protectionCost;
  const tax = subtotal * 0.08;
  const total = subtotal + tax;
  const securityDeposit = 500;

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.pickupDate || !form.returnDate) {
      toast({ title: 'Dates Required', description: 'Please select pickup and return dates.', variant: 'destructive' });
      return;
    }
    if (!form.ageConfirmed) {
      toast({ title: 'Age Confirmation Required', description: 'You must confirm that you are 25 years or older.', variant: 'destructive' });
      return;
    }
    if (!form.agreeTerms) {
      toast({ title: 'Terms Required', description: 'Please agree to the rental terms and conditions.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const pickupDateObj = new Date(form.pickupDate);
      const hh = String(pickupDateObj.getHours()).padStart(2, '0');
      const mm = String(pickupDateObj.getMinutes()).padStart(2, '0');
      const bookingData = {
        vehicleId: vehicleId,
        vehicleName: vehicle.vehicle_name,
        serviceType: 'rental',
        pickupLocation: 'ACE Garage — Atlanta, GA (confirmed at pickup)',
        pickupTime: `${hh}:${mm}`,
        checkIn: pickupDateObj.toISOString(),
        checkOut: new Date(form.returnDate).toISOString(),
        pickupDate: pickupDateObj.toISOString(),
        returnDate: new Date(form.returnDate).toISOString(),
        days,
        dailyRate,
        mileagePackage: pkg.label,
        mileageCost,
        protectionAddon: form.protectionAddon,
        protectionCost,
        subtotal,
        tax,
        totalPrice: total,
        securityDeposit,
        status: 'pending',
        customerInfo: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          driversLicense: form.driversLicense,
        },
      };
      await createBooking(bookingData);
      toast({ title: 'Rental Request Submitted', description: 'ACE will contact you within 1 business hour to confirm availability and arrange pickup.' });
      navigate('/booking/success?no_payment=true');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : (detail || err.message || 'Please try again or call (678) 846-3009.');
      toast({ title: 'Submission Failed', description: msg, variant: 'destructive' });
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-red-500 border-t-transparent rounded-full" /></div>;
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="rental-vehicle-missing">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-black mb-3">Rental Vehicle Not Found</h1>
          <p className="text-gray-500 mb-6">This rental is not currently available for direct booking.</p>
          <Button onClick={() => navigate('/fleet-gallery')} variant="outline">Browse Fleet</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="rental-booking-page">
      <div className="bg-black text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <button onClick={() => navigate(`/fleet/${vehicleId}`)} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-4" data-testid="back-to-vehicle-btn">
            <ArrowLeft className="h-4 w-4" /> Back to {vehicle.vehicle_name}
          </button>
          <p className="text-[10px] font-bold text-red-500 tracking-[0.3em] uppercase mb-2">Self-Drive Rental</p>
          <h1 className="text-3xl sm:text-4xl font-bold" data-testid="rental-booking-title">Reserve {vehicle.vehicle_name}</h1>
          <p className="text-gray-400 text-sm mt-2">Daily rate ${dailyRate} · 200 mi/day included · 25+ age required · CC hold for security deposit</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dates */}
            <Card className="p-6" data-testid="rental-dates-card">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="h-5 w-5 text-red-600" />
                <h2 className="text-lg font-bold text-black">Rental Dates</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pickupDate" className="text-xs font-semibold text-gray-600">Pickup Date & Time *</Label>
                  <Input id="pickupDate" type="datetime-local" value={form.pickupDate} onChange={e => update('pickupDate', e.target.value)} required data-testid="rental-pickup-input" />
                </div>
                <div>
                  <Label htmlFor="returnDate" className="text-xs font-semibold text-gray-600">Return Date & Time *</Label>
                  <Input id="returnDate" type="datetime-local" value={form.returnDate} onChange={e => update('returnDate', e.target.value)} required data-testid="rental-return-input" />
                </div>
              </div>
              {days > 0 && (
                <p className="text-sm text-gray-500 mt-3">Duration: <span className="font-bold text-black">{days} day{days > 1 ? 's' : ''}</span></p>
              )}
            </Card>

            {/* Mileage Package */}
            <Card className="p-6" data-testid="rental-mileage-card">
              <div className="flex items-center gap-2 mb-4">
                <Gauge className="h-5 w-5 text-red-600" />
                <h2 className="text-lg font-bold text-black">Mileage Package</h2>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {MILEAGE_PACKAGES.map(p => {
                  const selected = form.mileagePackage === p.id;
                  return (
                    <button type="button" key={p.id} onClick={() => update('mileagePackage', p.id)} className={`text-left p-4 rounded-lg border-2 transition-all ${selected ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-gray-400'}`} data-testid={`mileage-${p.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-sm text-black">{p.label}</span>
                        {selected && <Check className="h-4 w-4 text-red-600" />}
                      </div>
                      <p className="text-xs text-gray-500">{p.detail}</p>
                      <p className="text-sm font-bold text-black mt-2">{p.price === 0 ? 'Included' : `+$${p.price}/day`}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-400 mt-3">Additional miles beyond your package are billed at $1.50/mi.</p>
            </Card>

            {/* Protection */}
            <Card className="p-6" data-testid="rental-protection-card">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.protectionAddon} onCheckedChange={v => update('protectionAddon', !!v)} className="mt-1" data-testid="protection-checkbox" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-red-600" />
                    <span className="font-bold text-sm text-black">ACE Protection</span>
                    <span className="text-sm font-bold text-red-600">+$45/day</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Collision damage waiver + roadside assistance. Waives the $2,500 damage deductible. Recommended if your personal policy does not cover rentals.</p>
                </div>
              </label>
            </Card>

            {/* Driver Info */}
            <Card className="p-6" data-testid="rental-driver-card">
              <div className="flex items-center gap-2 mb-4">
                <User className="h-5 w-5 text-red-600" />
                <h2 className="text-lg font-bold text-black">Primary Driver</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-gray-600">First Name *</Label>
                  <Input value={form.firstName} onChange={e => update('firstName', e.target.value)} required data-testid="first-name-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600">Last Name *</Label>
                  <Input value={form.lastName} onChange={e => update('lastName', e.target.value)} required data-testid="last-name-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600"><Mail className="inline h-3 w-3 mr-1" />Email *</Label>
                  <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} required data-testid="email-input" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-600"><Phone className="inline h-3 w-3 mr-1" />Phone *</Label>
                  <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} required data-testid="phone-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-gray-600">Driver's License # *</Label>
                  <Input value={form.driversLicense} onChange={e => update('driversLicense', e.target.value)} required data-testid="license-input" placeholder="As shown on your license" />
                </div>
              </div>
              <label className="flex items-start gap-3 mt-4 cursor-pointer">
                <Checkbox checked={form.ageConfirmed} onCheckedChange={v => update('ageConfirmed', !!v)} className="mt-1" data-testid="age-confirm-checkbox" />
                <span className="text-xs text-gray-600">I confirm I am 25 years or older and hold a valid U.S. driver's license. Proof will be required at pickup.</span>
              </label>
            </Card>

            {/* Terms */}
            <Card className="p-6 bg-amber-50 border-amber-200" data-testid="rental-terms-card">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-sm text-amber-900 mb-2">Rental Terms Summary</h3>
                  <ul className="text-xs text-amber-900 space-y-1 list-disc list-inside">
                    <li>$500 security deposit (credit card hold, released at return)</li>
                    <li>Full coverage insurance on file OR ACE Protection add-on required</li>
                    <li>Return with the same fuel level — refueling fee $9/gal</li>
                    <li>No smoking. Cleaning fee $350 if required.</li>
                    <li>Late return fee: 1 hr grace, then $50/hr up to a full day.</li>
                  </ul>
                  <label className="flex items-start gap-2 mt-3 cursor-pointer">
                    <Checkbox checked={form.agreeTerms} onCheckedChange={v => update('agreeTerms', !!v)} className="mt-0.5" data-testid="agree-terms-checkbox" />
                    <span className="text-xs font-semibold text-amber-900">I agree to the ACE Self-Drive Rental Terms.</span>
                  </label>
                </div>
              </div>
            </Card>
          </div>

          {/* Summary sidebar */}
          <div>
            <Card className="p-6 sticky top-6 border-2 border-red-600" data-testid="rental-summary-card">
              <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
                <Car className="h-5 w-5 text-red-600" /> Reservation Summary
              </h3>
              <div className="space-y-2 text-sm pb-4 border-b border-gray-100">
                <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-semibold text-right">{vehicle.vehicle_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Duration</span><span className="font-semibold">{days} day{days !== 1 ? 's' : ''}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Daily Rate</span><span className="font-semibold">${dailyRate}</span></div>
              </div>
              <div className="space-y-2 text-sm py-4 border-b border-gray-100">
                <div className="flex justify-between"><span className="text-gray-500">Base (${dailyRate} × {days})</span><span>${dailyRate * days}</span></div>
                {mileageCost > 0 && <div className="flex justify-between"><span className="text-gray-500">{pkg.label} mileage</span><span>${mileageCost}</span></div>}
                {protectionCost > 0 && <div className="flex justify-between"><span className="text-gray-500">ACE Protection</span><span>${protectionCost}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-semibold">${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Tax (8%)</span><span>${tax.toFixed(2)}</span></div>
              </div>
              <div className="flex justify-between items-end py-4 border-b border-gray-100">
                <span className="font-bold text-black">Total</span>
                <span className="text-2xl font-bold text-red-600" data-testid="rental-total">${total.toFixed(2)}</span>
              </div>
              <div className="pt-4 text-[11px] text-gray-500 flex items-start gap-2">
                <CreditCard className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>Plus ${securityDeposit} refundable security deposit (credit card hold).</span>
              </div>

              <Button type="submit" disabled={submitting} className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-bold mt-6" data-testid="submit-rental-btn">
                {submitting ? 'Submitting…' : 'Request Reservation'}
              </Button>
              <p className="text-[11px] text-gray-400 text-center mt-2">No charge until confirmed by ACE.</p>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RentalBooking;
